import type { DesignBody, ResolvedEditAreaDefinition } from '@repo/api-contracts';
import type { CreateContext, MeasureText, RenderMaterials } from '@repo/design-renderer';
import type { Raster } from '@repo/warp-engine';
import type { ReactElement } from 'react';
import {
  type BoxNode,
  type FlatFitMode,
  faceSurfacesOf,
  layersOf,
  useEditorStore,
} from '@/features/design-editor';
import { FacePhotoOnly } from './face-photo-only';
import { FlatEditCanvas } from './flat-edit-canvas';
import { WarpedEditCanvas } from './warped-edit-canvas';

interface DesignCanvasProps {
  readonly definition: ResolvedEditAreaDefinition;
  readonly materials: RenderMaterials;
  /** 선택 중 안내선 — 평면 면이면 영역 mm 로 편 래스터, 곡면 면이면 사진 px 래스터(휜 캔버스가 사진 상자에 겹친다). */
  readonly guide: Raster | null;
  readonly createContext: CreateContext;
  readonly measure: MeasureText;
  readonly onChangeNode: (node: BoxNode) => void;
  readonly onNudge: (dxMm: number, dyMm: number) => void;
  readonly onDeleteSelected: () => void;
  /** 캔버스 위 글자 바로 고치기 — 새 글자와 이번 고치기의 이름(같은 이름은 undo 한 번으로 묶는다). */
  readonly onTextInput: (nodeId: string, text: string, session: string) => void;
  /** 바로 고치기를 열어 달라는 요청(텍스트를 새로 넣었을 때). seq 가 바뀔 때마다 연다. */
  readonly editRequest?: { readonly nodeId: string; readonly seq: number } | null;
  readonly onBackdropError: () => void;
  /** 곡면 상품의 실제 합성(손을 멈추면 뒤에서 그린다). 평면 상품은 null. */
  readonly warped: ImageBitmap | null;
  /** 곡면 상품을 끄는 동안 본문만 바꿔 낮은 해상도로 다시 그린 합성. 그릴 수 없으면 null. */
  readonly renderLive: (body: DesignBody) => Raster | null;
  readonly onWarpedHit: (photoPx: { x: number; y: number }) => string | null;
  /** 보기 모드(전체 보기/영역 확대) — 스테이지의 토글이 정하고, 캔버스가 휠로 바꾸면 알린다. */
  readonly fitMode: FlatFitMode;
  readonly onFitModeChange: (mode: FlatFitMode) => void;
}

/**
 * 목업 위 바로 편집 — 지금 색상·면의 목업 사진 위에 그 면의 인쇄 영역 전부를 제자리에 겹친다(마플식).
 * 편집↔미리보기 전환이 없어 선택을 풀어도 화면이 그대로다. 영역이 여럿이어도 탭 없이 한 화면에서 누르는 대로 편집한다.
 * 편집 중인 영역이 곡면(원통·격자)이고 사진이 있으면 휜 모습 그대로 편집한다(WarpedEditCanvas, U2).
 */
export const DesignCanvas = ({
  definition,
  materials,
  guide,
  createContext,
  measure,
  onChangeNode,
  onNudge,
  onDeleteSelected,
  onTextInput,
  editRequest = null,
  onBackdropError,
  warped,
  renderLive,
  onWarpedHit,
  fitMode,
  onFitModeChange,
}: DesignCanvasProps): ReactElement => {
  const { document, faceKey, componentId, selectedNodeId, setComponent, select, clearSelection } =
    useEditorStore();
  const faceSurfaces = faceSurfacesOf({
    definition,
    colorOptionId: document.colorOptionId,
    faceKey,
  });
  const surfaces = faceSurfaces.slots.map((slot) => ({
    ...slot,
    layers: layersOf(document, slot.component.id),
  }));
  const activeComponentId =
    surfaces.find((surface) => surface.component.id === componentId)?.component.id ??
    surfaces[0]?.component.id ??
    '';
  // 곡면 미리보기(useWarpedPreview)도 편집 중인 영역의 표면으로 켜고 끈다 — 같은 기준으로 고른다.
  const activeSurface = surfaces.find((surface) => surface.component.id === activeComponentId);
  const backdrop = faceSurfaces.backdrop;
  const shared = {
    surfaces,
    space: faceSurfaces.space,
    activeComponentId,
    onActivate: setComponent,
    selectedNodeId,
    materials,
    guide,
    createContext,
    measure,
    backdrop,
    onSelect: (id: string | null) => (id === null ? clearSelection() : select(id)),
    onChangeNode,
    onNudge,
    onDeleteSelected,
    onTextInput,
    editRequest,
    onBackdropError,
    onWarpedHit,
    fitMode,
    onFitModeChange,
  };

  return (
    <div className="relative size-full">
      <div className="absolute inset-0">
        {surfaces.length === 0 ? (
          <FacePhotoOnly url={backdrop?.url ?? null} />
        ) : backdrop !== null &&
          activeSurface !== undefined &&
          activeSurface.surface.type !== 'flat' ? (
          <WarpedEditCanvas
            {...shared}
            photo={faceSurfaces.space}
            warped={warped}
            renderLive={renderLive}
            sceneKey={`${faceKey}:${document.colorOptionId}`}
          />
        ) : (
          <FlatEditCanvas {...shared} warped={warped} />
        )}
      </div>
    </div>
  );
};
